
import { computed, defineComponent, ref } from "vue";
import AppSection from "@/components/common/AppSection.vue";
import { useStore } from "vuex";
import AppUpload from "@/components/common/AppUpload.vue";
import AppButton from "@/components/common/AppButton.vue";
import SettingModel, { CustomFont } from "@/models/SettingModel";
import AppInput from "@/components/common/AppInput.vue";
import ColorPicker from "primevue/colorpicker";
import axios from "axios";
import { useToast } from "primevue/usetoast";
import {
    createBrandColorSlots,
    isValidBrandColor,
    normalizeBrandColor
} from "@/config/brandColors";
import { useI18n } from "vue-i18n";

export default defineComponent({
    name: "SettingsBrandPage",
    components: { AppInput, AppButton, AppUpload, AppSection, ColorPicker },
    setup() {
        const store = useStore();
        const toast = useToast();
        const { t } = useI18n();

        const setBrandCustomized = () => {
            const brandCustomized: SettingModel = {
                id:
                    store.state.settings.list.find(
                        (setting: SettingModel) =>
                            setting.key === "brand_customized"
                    )?.id || 0,
                key: "brand_customized",
                value: true,
                type: "bool"
            };
            return store.dispatch(
                "settings/createOrUpdateSetting",
                brandCustomized
            );
        };

        const defaultAudience = store.getters["audiences/getDefault"]();
        const brandColorsSetting = computed((): SettingModel | undefined =>
            store.getters["settings/getSettingByKey"]("brand_colors")
        );
        const brandColors = ref(
            createBrandColorSlots(
                defaultAudience.color,
                brandColorsSetting.value?.value
            )
        );

        const isBrandColorInvalid = (index: number): boolean => {
            const color = normalizeBrandColor(brandColors.value[index]);

            return index === 0
                ? !isValidBrandColor(color)
                : color !== "" && !isValidBrandColor(color);
        };

        const clearBrandColor = (index: number) => {
            brandColors.value[index] = "";
        };

        const changeColors = async () => {
            if (
                brandColors.value.some((_, index) => isBrandColorInvalid(index))
            ) {
                toast.add({
                    severity: "error",
                    summary: t("pages.settings.brand.invalidColorSummary"),
                    detail: t("pages.settings.brand.invalidColor"),
                    life: 3000
                });
                return;
            }

            const normalizedColors = brandColors.value.map(normalizeBrandColor);
            const setting: SettingModel = {
                id: brandColorsSetting.value?.id || 0,
                key: "brand_colors",
                value: normalizedColors,
                type: "json"
            };

            try {
                await Promise.all([
                    axios.patch(`/api/categories/${defaultAudience.id}/edit`, {
                        color: normalizedColors[0]
                    }),
                    store.dispatch("settings/createOrUpdateSetting", setting)
                ]);
                brandColors.value = normalizedColors;
                await Promise.all([
                    store.dispatch("audiences/fetchAudiences"),
                    store.dispatch("settings/fetchList"),
                    setBrandCustomized()
                ]);
                toast.add({
                    severity: "success",
                    summary: t("pages.settings.brand.colorsUpdated"),
                    detail: t("pages.settings.brand.colorsUpdatedText"),
                    life: 3000
                });
            } catch {
                toast.add({
                    severity: "error",
                    summary: t("pages.settings.brand.colorsUpdateFailed"),
                    detail: t("pages.settings.brand.tryAgain"),
                    life: 3000
                });
            }
        };

        const uploading = ref(false);

        const logo = computed((): SettingModel | undefined =>
            store.getters["settings/getSettingByKey"]("logo")
        );

        const logoReset = ref(false);
        const showLogo = computed((): boolean => {
            return !!logo.value && !logoReset.value && !uploading.value;
        });

        const uploadLogo = (files: File[]) => {
            if (files.length) {
                uploading.value = true;
                const fileReader = new FileReader();
                fileReader.readAsDataURL(files[0]);
                fileReader.onload = function(evt) {
                    return evt.target?.result;
                };

                const reader = new FileReader();
                reader.onloadend = () => {
                    if (reader.result !== "") {
                        const uploadFile: SettingModel = {
                            id: logo.value?.id || 0,
                            key: "logo",
                            value: reader.result as string,
                            type: "file"
                        };

                        store
                            .dispatch(
                                "settings/createOrUpdateSetting",
                                uploadFile
                            )
                            .finally(() => {
                                store
                                    .dispatch("settings/fetchList")
                                    .finally(() => {
                                        logoReset.value = false;
                                        uploading.value = false;
                                    });
                                setBrandCustomized();
                            });
                    }
                };
                reader.readAsDataURL(files[0]);
            }
        };

        async function resetLogo() {
            logoReset.value = true;
        }

        const remove = () => {
            logoReset.value = false;
        };

        const customFontSetting = computed((): SettingModel | undefined =>
            store.state.settings.list.find(
                (setting: SettingModel) => setting.key === "custom_font"
            )
        );

        const customFont = computed(
            (): CustomFont | undefined =>
                customFontSetting.value?.value as CustomFont
        );

        const customFontName = ref(customFont.value?.name || "");
        const customFontFamily = ref(customFont.value?.fontFamily || "");
        const customFontCssUrl = ref(customFont.value?.url || "");

        const updateCustomFont = () => {
            const customFont: SettingModel = {
                id: customFontSetting.value?.id || 0,
                key: "custom_font",
                value: {
                    name: customFontName.value,
                    fontFamily: customFontFamily.value,
                    url: customFontCssUrl.value
                },
                type: "json"
            };
            store
                .dispatch("settings/createOrUpdateSetting", customFont)
                .finally(() => {
                    store.dispatch("settings/fetchList").finally(() => {
                        toast.add({
                            severity: "success",
                            summary: "Font updated",
                            detail: `Your font has been successfully updated.`,
                            life: 3000
                        });
                    });
                    setBrandCustomized();
                });
        };

        return {
            logo,
            resetLogo,
            showLogo,
            uploadLogo,
            remove,
            logoReset,
            brandColors,
            isBrandColorInvalid,
            clearBrandColor,
            changeColors,
            customFontName,
            customFontFamily,
            customFontCssUrl,
            updateCustomFont
        };
    }
});
